/**
 * Lấy thông tin chi tiết của khóa học bao gồm tất cả các nodes
 * @param courseId ID của khóa học
 * @returns Thông tin chi tiết của khóa học
 */

import { supabase } from '@/lib/supabase';
import { LessonResources, DiagramResource, QuizResource } from '../types/courseTypes';
import { Course } from '@/types/course';

export interface Course {
  id: string;
  name: string;
  title: string;
  description: string;
  image: string;
  image_url: string;
  duration?: string;
  estimated_time?: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  category?: string;
  category_color?: string;
  categories?: string[];
  category_id?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  expert_id?: string | null;
  price: number;
  credit_cost: number;
  is_free: boolean;
  featured: boolean;
  status?: string;
  students_count: number;
  rating: number;
  total_lessons: number;
  expert_details?: {
    id: string;
    name: string;
    avatar_url: string;
    bio: string;
    specialties: string[];
  } | null;
  tags?: string[];
  content?: any;
  course_materials?: any;
}

// Lấy tất cả khóa học
export const getAllCourses = async (): Promise<Course[]> => {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      id,
      name,
      description,
      image,
      category:categories(id, name, color),
      difficulty,
      status,
      created_at,
      updated_at,
      category_id,
      expert_id,
      price,
      featured,
      estimated_time,
      students_count,
      rating,
      total_lessons,
      expert_details
    `)
    .eq('status', 'published')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }

  // Format response để đảm bảo các trường bắt buộc
  return data?.map(course => ({
    ...course,
    image: course.image || null, // Đảm bảo luôn có giá trị cho image
    price: typeof course.price === 'number' ? course.price : 0,
    students_count: course.students_count || 0,
    rating: course.rating || 0,
    total_lessons: course.total_lessons || 0
  })) || [];
};

// Lấy khóa học nổi bật
export const getFeaturedCourses = async (limit: number = 5): Promise<Course[]> => {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      category:categories(id, name, color)
    `)
    .eq('status', 'published')
    .eq('featured', true)
    .order('rating', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching featured courses:', error);
    throw error;
  }

  return data?.map(formatCourseResponse) || [];
};

// Lấy khóa học phổ biến
export const getPopularCourses = async (limit: number = 5): Promise<Course[]> => {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      category:categories(id, name, color)
    `)
    .eq('status', 'published')
    .order('students_count', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching popular courses:', error);
    throw error;
  }

  return data?.map(formatCourseResponse) || [];
};

// Format response từ database thành Course type
const formatCourseResponse = (course: any): Course => {
  return {
    id: course.id,
    name: course.name,
    title: course.name,
    description: course.description,
    image_url: course.image,
    image: course.image,
    duration: formatDuration(course.estimated_time),
    estimated_time: course.estimated_time,
    level: course.difficulty,
    category: course.category?.name || '',
    category_color: course.category?.color,
    categories: [course.category?.name || ''],
    price: course.price || 0,
    credit_cost: course.credit_cost || 0,
    is_free: course.price === 0 || course.is_free === true,
    featured: course.featured || false,
    students_count: course.students_count || 0,
    rating: course.rating || 0,
    total_lessons: course.total_lessons || 0,
    expert_details: course.expert_details || null,
    tags: course.tags || [],
    content: course.content || null,
    course_materials: course.course_materials || null
  };
};

// Format duration từ estimated_time string sang minutes
export const formatDuration = (estimatedTime: string): string => {
  if (!estimatedTime) return '0 phút';
  
  // Parse "4-6 giờ" format
  const matches = estimatedTime.match(/(\d+)-(\d+)\s*(giờ|phút)/i);
  if (matches) {
    const [_, min, max, unit] = matches;
    const avgTime = (parseInt(min) + parseInt(max)) / 2;
    if (unit.toLowerCase() === 'giờ') {
      return `${Math.round(avgTime)} giờ`;
    }
    return `${Math.round(avgTime)} phút`;
  }
  
  // Parse "45 phút" format
  const simpleMatch = estimatedTime.match(/(\d+)\s*(giờ|phút)/i);
  if (simpleMatch) {
    const [_, time, unit] = simpleMatch;
    return `${time} ${unit.toLowerCase()}`;
  }
  
  return estimatedTime;
};

export const getCoursesByCategory = async (categoryId: string): Promise<Course[]> => {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      category:categories(id, name)
    `)
    .eq('status', 'published')
    .eq('category_id', categoryId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching courses by category:', error);
    throw error;
  }

  return data || [];
};

export const getCourseById = async (courseId: string): Promise<Course | null> => {
  try {
    console.log(`Fetching course data for ID: ${courseId}`);
    
    // Lấy thông tin cơ bản của khóa học
    const { data: courseData, error: courseError } = await supabase
      .from('courses')
      .select(`
        *,
        category:categories(id, name, color),
        expert_details
      `)
      .eq('id', courseId)
      .single();

    if (courseError) {
      console.error('Error fetching course:', courseError);
      return null;
    }

    // Lấy nội dung khóa học từ bảng course_contents
    const { data: contentData, error: contentError } = await supabase
      .from('course_contents')
      .select('content')
      .eq('course_id', courseId)
      .single();

    if (contentError) {
      console.error('Error fetching course content:', contentError);
    }

    // Format response với nội dung nếu có
    const formattedCourse = formatCourseResponse({
      ...courseData,
      content: contentData?.content || null
    });

    return formattedCourse;
  } catch (error) {
    console.error(`Error fetching course ${courseId}:`, error);
    return null;
  }
};

/**
 * Trích xuất dữ liệu khóa học từ DOM
 */
const extractCourseDataFromDOM = (courseId: string) => {
  try {
    // Lấy tên khóa học từ tiêu đề trang
    const courseTitle = document.querySelector('h1')?.textContent || 
                        document.title.split('|')[0].trim() || 
                        'Khóa học';
    
    // Lấy mô tả khóa học
    const courseDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || 
                             document.querySelector('.course-description')?.textContent?.trim() ||
                             'Mô tả khóa học';
    
    // Tìm tất cả các nodes từ sidebar hoặc menu
    const nodeElements = document.querySelectorAll('.course-sidebar a, .course-menu a, .course-outline a, .course-nodes a');
    
    // Nếu không tìm thấy, thử tìm theo cách khác - tìm các phần tử có class chứa từ khóa
    const alternativeNodeElements = nodeElements.length === 0 
      ? document.querySelectorAll('[class*="lesson"], [class*="node"], [class*="chapter"]')
      : [];
    
    // Nếu vẫn không tìm thấy, thử tìm theo cấu trúc DOM cụ thể của trang
    const specificNodeElements = (nodeElements.length === 0 && alternativeNodeElements.length === 0)
      ? extractNodesFromCurrentPage()
      : [];
    
    // Tạo danh sách nodes
    const nodes = [];
    
    // Xử lý các node elements
    const elements = nodeElements.length > 0 
      ? nodeElements 
      : (alternativeNodeElements.length > 0 ? alternativeNodeElements : specificNodeElements);
    
    elements.forEach((element, index) => {
      const nodeName = element.textContent?.trim() || `Bài ${index + 1}`;
      const nodeId = element.getAttribute('href')?.split('/').pop() || 
                    element.getAttribute('data-id') || 
                    `${courseId}-node-${index + 1}`;
      
      nodes.push({
        id: nodeId,
        name: nodeName,
        content: `Nội dung của ${nodeName}`,
        type: 'lesson'
      });
    });
    
    // Nếu không tìm thấy nodes từ DOM, thử lấy từ nội dung trang
    if (nodes.length === 0) {
      // Tìm các tiêu đề có thể là tên bài học
      const headings = document.querySelectorAll('h2, h3');
      headings.forEach((heading, index) => {
        const nodeName = heading.textContent?.trim() || `Bài ${index + 1}`;
        const nodeId = `${courseId}-node-${index + 1}`;
        const nextHeading = headings[index + 1];
        let content = '';
        
        // Lấy nội dung giữa heading hiện tại và heading tiếp theo
        let currentElement = heading.nextElementSibling;
        while (currentElement && currentElement !== nextHeading) {
          content += currentElement.textContent || '';
          currentElement = currentElement.nextElementSibling;
        }
        
        nodes.push({
          id: nodeId,
          name: nodeName,
          content: content || `Nội dung của ${nodeName}`,
          type: 'lesson'
        });
      });
    }
    
    // Nếu vẫn không tìm thấy nodes, trả về null để sử dụng phương pháp khác
    if (nodes.length === 0) {
      return null;
    }
    
    return {
      id: courseId,
      name: courseTitle,
      description: courseDescription,
      nodes: nodes
    };
  } catch (error) {
    console.error('Error extracting course data from DOM:', error);
    return null;
  }
};

/**
 * Trích xuất nodes từ cấu trúc trang hiện tại
 */
const extractNodesFromCurrentPage = () => {
  try {
    // Tìm các phần tử có thể chứa danh sách bài học
    const possibleContainers = [
      // Tìm theo class name
      document.querySelectorAll('.course-content, .course-outline, .course-lessons, .course-modules'),
      // Tìm theo role
      document.querySelectorAll('[role="list"], [role="listbox"], [role="menu"]'),
      // Tìm theo thẻ
      document.querySelectorAll('ul, ol')
    ];
    
    // Lấy container đầu tiên có nhiều hơn 1 phần tử con
    let container = null;
    for (const containerList of possibleContainers) {
      for (const possibleContainer of containerList) {
        if (possibleContainer.children.length > 1) {
          container = possibleContainer;
          break;
        }
      }
      if (container) break;
    }
    
    if (!container) {
      console.warn('No suitable container found for nodes');
      return [];
    }
    
    // Lấy các phần tử con làm nodes
    const nodeElements = [];
    for (let i = 0; i < container.children.length; i++) {
      const child = container.children[i];
      // Kiểm tra xem phần tử có phải là một node hợp lệ không
      if (child.textContent && child.textContent.trim()) {
        nodeElements.push(child);
      }
    }
    
    // Nếu không tìm thấy nodes, thử tìm theo cách khác
    if (nodeElements.length === 0) {
      // Tìm tất cả các phần tử có thể là node
      const allPossibleNodes = document.querySelectorAll('li, .item, .card, .node, .lesson');
      for (const node of allPossibleNodes) {
        if (node.textContent && node.textContent.trim()) {
          nodeElements.push(node);
        }
      }
    }
    
    return nodeElements;
  } catch (error) {
    console.error('Error extracting nodes from current page:', error);
    return [];
  }
};

/**
 * Lấy tài nguyên (diagrams, quiz) của bài học
 * @param lessonId ID của bài học
 */
export const getLessonResources = async (lessonId: string): Promise<LessonResources> => {
  try {
    const { data, error } = await supabase
      .from('lesson_resources')
      .select('*')
      .eq('lesson_id', lessonId);
      
    if (error) throw error;
    
    // Nhóm resources theo loại
    const resources: LessonResources = { diagrams: [], quiz: null };
    
    for (const resource of data || []) {
      if (resource.resource_type === 'diagram') {
        resources.diagrams.push({
          id: resource.resource_id,
          position: resource.position,
          showDetails: resource.config?.showDetails || false
        });
      } else if (resource.resource_type === 'quiz') {
        resources.quiz = {
          id: resource.resource_id,
          position: resource.position,
          required: resource.config?.required || false,
          minScore: resource.config?.minScore || 0,
          timeLimit: resource.config?.timeLimit || null
        };
      }
    }
    
    return resources;
  } catch (error) {
    console.error('Error fetching lesson resources:', error);
    return { diagrams: [], quiz: null };
  }
};

/**
 * Gán diagram cho bài học
 */
export const assignDiagramToLesson = async (
  lessonId: string,
  diagramId: string,
  position: string,
  showDetails: boolean
) => {
  try {
    const { data, error } = await supabase
      .from('lesson_resources')
      .upsert({
        lesson_id: lessonId,
        resource_type: 'diagram',
        resource_id: diagramId,
        position: position,
        config: { showDetails }
      }, { onConflict: 'lesson_id,resource_type,resource_id' });
      
    return { data, error };
  } catch (error) {
    console.error('Error assigning diagram to lesson:', error);
    return { data: null, error };
  }
};

/**
 * Xóa diagram khỏi bài học
 */
export const removeDiagramFromLesson = async (
  lessonId: string,
  diagramId: string
) => {
  try {
    const { data, error } = await supabase
      .from('lesson_resources')
      .delete()
      .match({
        lesson_id: lessonId,
        resource_type: 'diagram',
        resource_id: diagramId
      });
      
    return { data, error };
  } catch (error) {
    console.error('Error removing diagram from lesson:', error);
    return { data: null, error };
  }
};

/**
 * Gán quiz cho bài học
 */
export const assignQuizToLesson = async (
  lessonId: string,
  quizId: string,
  required: boolean = false,
  minScore: number = 0,
  timeLimit?: number
) => {
  try {
    // Trước tiên xóa quiz hiện tại nếu có
    await supabase
      .from('lesson_resources')
      .delete()
      .match({
        lesson_id: lessonId,
        resource_type: 'quiz'
      });
    
    // Sau đó thêm quiz mới
    const { data, error } = await supabase
      .from('lesson_resources')
      .insert({
        lesson_id: lessonId,
        resource_type: 'quiz',
        resource_id: quizId,
        position: 'end', // Quiz luôn ở cuối bài học
        config: { required, minScore, timeLimit }
      });
      
    return { data, error };
  } catch (error) {
    console.error('Error assigning quiz to lesson:', error);
    return { data: null, error };
  }
};

/**
 * Xóa quiz khỏi bài học
 */
export const removeQuizFromLesson = async (lessonId: string) => {
  try {
    const { data, error } = await supabase
      .from('lesson_resources')
      .delete()
      .match({
        lesson_id: lessonId,
        resource_type: 'quiz'
      });
      
    return { data, error };
  } catch (error) {
    console.error('Error removing quiz from lesson:', error);
    return { data: null, error };
  }
};

/**
 * Lấy danh sách tài nguyên diagram theo khóa học
 */
export const getDiagramsByCourse = async (courseId: string) => {
  try {
    // Lấy tất cả các bài học trong khóa học
    const { data: lessons, error: lessonError } = await supabase
      .from('lessons')
      .select('id')
      .eq('course_id', courseId);
      
    if (lessonError) throw lessonError;
    
    // Lấy tất cả diagram được gán cho các bài học
    const lessonIds = lessons.map(lesson => lesson.id);
    const { data: resources, error: resourceError } = await supabase
      .from('lesson_resources')
      .select('*')
      .in('lesson_id', lessonIds)
      .eq('resource_type', 'diagram');
      
    if (resourceError) throw resourceError;
    
    return resources;
  } catch (error) {
    console.error('Error fetching diagrams by course:', error);
    return [];
  }
};

/**
 * Lấy danh sách tài nguyên quiz theo khóa học
 */
export const getQuizzesByCourse = async (courseId: string) => {
  try {
    // Lấy tất cả các bài học trong khóa học
    const { data: lessons, error: lessonError } = await supabase
      .from('lessons')
      .select('id')
      .eq('course_id', courseId);
      
    if (lessonError) throw lessonError;
    
    // Lấy tất cả quiz được gán cho các bài học
    const lessonIds = lessons.map(lesson => lesson.id);
    const { data: resources, error: resourceError } = await supabase
      .from('lesson_resources')
      .select('*')
      .in('lesson_id', lessonIds)
      .eq('resource_type', 'quiz');
      
    if (resourceError) throw resourceError;
    
    return resources;
  } catch (error) {
    console.error('Error fetching quizzes by course:', error);
    return [];
  }
};

export const courseService = {
  getCourseById,
  getLessonResources,
  assignDiagramToLesson,
  removeDiagramFromLesson,
  assignQuizToLesson,
  removeQuizFromLesson,
  getDiagramsByCourse,
  getQuizzesByCourse,
  getAllCourses,
  getFeaturedCourses,
  getPopularCourses,
  getCoursesByCategory,
  formatDuration
}; 